import {
  CheckCircle,
  Email,
  Facebook,
  Instagram,
  IosShare,
  Language,
  NotificationsActiveOutlined,
  ReportOutlined,
  X,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Link,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { AppSnackbar } from "@root/components/common/AppSnackbar";
import { ErrorMessage } from "@root/components/common/ErrorMessage";
import { LoadingSpinner } from "@root/components/common/LoadingSpinner";
import { EventList } from "@root/components/EventList";
import { ConfirmModal } from "@root/components/modals/ConfirmModal";
import { ReportEventModal } from "@root/components/modals/ReportEventModal";
import { ShareModal } from "@root/components/modals/ShareModal";
import { organizationService } from "@root/services/api/organizationService";
import { subscriptionService } from "@root/services/api/subscriptionService";
import { userService } from "@root/services/api/userService";
import { useAccountContext } from "@root/stores/accountStore";
import { ModalStore } from "@root/stores/modalStore";
import { PaginationStore } from "@root/stores/paginationStore";
import { Event, Organization } from "@root/utils/entities";
import noPreviewUrl from "@images/no-preview.jpg";
import locationIcon from "@images/location.svg";
import {
  getApiError,
  getOrganizationShareUrl,
  hasErrors,
} from "@root/utils/functions";
import { useSnackbar } from "@root/hooks/useSnackbar";
import { observer, useLocalObservable } from "mobx-react-lite";
import {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

type OrganizationViewProps = {
  organization: Organization;
  onViewEvent: (event: Event) => void;
};

type PopupViewState = {
  confirmTitle?: string;
  confirmMessage?: string;
  onConfirm: () => Promise<void>;
};

export const OrganizationView = observer(
  ({ organization, onViewEvent }: OrganizationViewProps) => {
    const accountStore = useAccountContext();

    const confirmModalStore = useLocalObservable(() => new ModalStore());
    const shareModalStore = useLocalObservable(() => new ModalStore());
    const reportModalStore = useLocalObservable(() => new ModalStore());
    const paginationStore = useLocalObservable(() => new PaginationStore());

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [popupState, setPopupState] = useState<PopupViewState>();
    const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
    const [events, setEvents] = useState<Event[]>([]);
    const [isFollowing, setIsFollowing] = useState<boolean>(false);
    const [hasActiveSubscription, setHasActiveSubscription] =
      useState<boolean>(false);
    const [isCheckingSubscription, setIsCheckingSubscription] =
      useState<boolean>(true);

    const accountId = useMemo(
      () => accountStore.account?.id ?? "",
      [accountStore.account],
    );
    const organizationId = useMemo(() => organization.id, [organization]);

    // True only when the current session IS this organization (viewing as the org itself).
    // Owner/members visiting from their personal account can still follow the org.
    const isOwnOrganization = useMemo(() => {
      return (
        accountStore.session?.type === "organization" &&
        accountStore.session?.id === organization.id
      );
    }, [accountStore.session, organization.id]);

    // Verified badge: shown when this organization has an active paid subscription.
    // Same logic as EventView — plan list price decides "paid", handling
    // coupon-zero purchases (buyingPrice=0 but plan price > 0).
    useEffect(() => {
      const checkSubscriptionStatus = async () => {
        setIsCheckingSubscription(true);
        try {
          const response =
            await subscriptionService.getCurrentByOrganizationIdWithDetails(
              organizationId,
            );
          const subscription = response?.data as any;
          const isActive =
            subscription?.isActive === true || subscription?.isActive === 1;
          const pricing =
            subscription?.pricing ?? subscription?.planVersion ?? {};
          const isPaidSubscription =
            (pricing.monthlyPrice ?? 0) > 0 || (pricing.annualPrice ?? 0) > 0;
          setHasActiveSubscription(!!isActive && isPaidSubscription);
        } catch (error) {
          setHasActiveSubscription(false);
        } finally {
          setIsCheckingSubscription(false);
        }
      };
      checkSubscriptionStatus();
    }, [organizationId]);

    useEffect(() => {
      if (!accountStore.account || !accountId) return;
      userService.getFollowedOrganizations(accountId).then((response) => {
        if (!hasErrors(response)) {
          const orgs = response.data?.organizations ?? [];
          setIsFollowing(orgs.some((o) => o.organizationId === organizationId));
        }
      });
    }, [accountId, organizationId, accountStore.account]);

    const followOrganization = useCallback(async () => {
      setLoading(true);

      const response = await userService.followOrganization(
        accountId,
        organizationId,
      );

      if (hasErrors(response)) {
        setError(getApiError(response).message);
      } else {
        setIsFollowing(true);
        showSnackbar("You're now following this organization.", "success");
        setError(undefined);
      }

      setLoading(false);
    }, [accountId, organizationId]);

    const unfollowOrganization = useCallback(async () => {
      setLoading(true);

      const response = await userService.unfollowOrganization(
        accountId,
        organizationId,
      );

      if (hasErrors(response)) {
        setError(getApiError(response).message);
      } else {
        setIsFollowing(false);
        showSnackbar("You've unfollowed this organization.", "success");
        setError(undefined);
      }

      setLoading(false);
    }, [accountId, organizationId]);

    const reportOrganization = useCallback(
      async (reason: string) => {
        setLoading(true);

        const response = await userService.reportOrganization(
          accountId,
          organizationId,
          reason,
        );

        if (hasErrors(response)) {
          setError(getApiError(response).message);
        } else {
          showSnackbar("You've reported this organization.", "success");
          setError(undefined);
        }

        setLoading(false);
      },
      [accountId, organizationId],
    );

    const showFollowPopup = useCallback(() => {
      if (accountStore.account) {
        if (isFollowing) {
          setPopupState({
            confirmTitle: "Unfollow Organization?",
            confirmMessage: "Would you like to unfollow this organization?",
            onConfirm: async () => {
              confirmModalStore.close();
              unfollowOrganization();
            },
          });
        } else {
          setPopupState({
            confirmTitle: "Follow Organization?",
            confirmMessage: "Would you like to follow this organization?",
            onConfirm: async () => {
              confirmModalStore.close();
              followOrganization();
            },
          });
        }
        confirmModalStore.open();
      } else {
        accountStore.authModalStore.openAuthModal("login");
      }
    }, [
      confirmModalStore,
      accountStore.account,
      accountStore.authModalStore,
      isFollowing,
      followOrganization,
      unfollowOrganization,
    ]);

    const showSharePopup = useCallback(
      () =>
        accountStore.account
          ? shareModalStore.open()
          : accountStore.authModalStore.openAuthModal("login"),
      [shareModalStore, accountStore.authModalStore, accountStore.account],
    );

    const showReportPopup = useCallback(() => {
      if (accountStore.account) {
        reportModalStore.open();
      } else {
        accountStore.authModalStore.openAuthModal("login");
      }
    }, [reportModalStore, accountStore.authModalStore, accountStore.account]);

    const loadEvents = useCallback(
      async (page: number = 0) => {
        setLoading(true);

        const response = await organizationService.getOrganizationEvents(
          organizationId,
          page,
        );

        if (hasErrors(response)) {
          setError(getApiError(response).message);
        } else {
          setEvents(response.data?.events ?? []);
          paginationStore.setPagination(
            response.data?.pagination?.page ?? 0,
            response.data?.pagination?.pageCount ?? 0,
            response.data?.pagination?.totalCount ?? 0,
          );
        }

        setLoading(false);
      },
      [organizationId, paginationStore],
    );

    const onPageChange = useCallback(
      async (newPage: number) => {
        await loadEvents(newPage);
      },
      [loadEvents],
    );

    useEffect(() => {
      if (!organizationId) return;

      setError(undefined);
      void loadEvents();
    }, [organizationId, loadEvents]);

    return (
      <>
        <Stack gap="0.75rem" sx={{ paddingBottom: "2rem" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "0.5rem 1rem",
              gap: "0.75rem",
              alignItems: "center",
            }}
          >
            <Avatar
              src={organization.imageUrl || noPreviewUrl}
              sx={{
                width: "4rem",
                height: "4rem",
                borderRadius: "50%",
                border: "1px solid #e0e0e0",
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <Typography
                variant="h2"
                sx={{ textAlign: "center", overflowWrap: "break-word" }}
              >
                {organization.name}
              </Typography>
              {!isCheckingSubscription && hasActiveSubscription && (
                <CheckCircle sx={{ fontSize: "1.25rem", color: "#4CAF50" }} />
              )}
            </Box>
          </Box>
          {error && <ErrorMessage message={error} />}
          <AppSnackbar
            open={snackbar.open}
            message={snackbar.message}
            severity={snackbar.severity}
            onClose={closeSnackbar}
          />
          <Separator />
          <Stack sx={{ gap: "0.75rem", padding: "0.5rem 1rem" }}>
            {organization.address && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1.25,
                }}
              >
                <Box
                  component="img"
                  src={locationIcon}
                  alt="Location"
                  sx={{ width: "1.25rem", height: "1.25rem", mt: 0.25 }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: "#000",
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {organization.name}
                  </Typography>
                  {[
                    organization.address.addressLine1,
                    organization.address.addressLine2,
                    [organization.address.city, organization.address.state]
                      .filter(Boolean)
                      .join(", "),
                    organization.address.zip,
                    organization.address.country === "US"
                      ? "United States"
                      : organization.address.country === "CA"
                        ? "Canada"
                        : organization.address.country,
                  ]
                    .filter(Boolean)
                    .map((line, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          color: "#757575",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                </Box>
              </Box>
            )}
            {organization.email && (
              <InfoRow>
                <Email sx={{ width: "1.5rem", height: "1.5rem" }} />
                <Link
                  href={`mailto:${organization.email}`}
                  sx={{ alignSelf: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      overflowWrap: "break-word",
                      alignSelf: "center",
                      color: theme.palette.primary.main,
                    })}
                  >
                    {organization.email}
                  </Typography>
                </Link>
              </InfoRow>
            )}
            {organization.website && (
              <InfoRow>
                <Language sx={{ width: "1.5rem", height: "1.5rem" }} />
                <Link
                  href={organization.website}
                  target="_blank"
                  sx={{ alignSelf: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      overflowWrap: "break-word",
                      alignSelf: "center",
                      color: theme.palette.primary.main,
                    })}
                  >
                    {organization.website}
                  </Typography>
                </Link>
              </InfoRow>
            )}
            {organization.facebookUrl && (
              <InfoRow>
                <Facebook
                  sx={{ width: "1.5rem", height: "1.5rem", color: "#316FF6" }}
                />
                <Link
                  href={organization.facebookUrl}
                  target="_blank"
                  sx={{ alignSelf: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      overflowWrap: "break-word",
                      alignSelf: "center",
                      color: theme.palette.primary.main,
                    })}
                  >
                    {organization.facebookUrl}
                  </Typography>
                </Link>
              </InfoRow>
            )}
            {organization.instagramUrl && (
              <InfoRow>
                <Instagram
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    background:
                      "linear-gradient(to right, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
                    color: "#FFF",
                    fontWeight: "bold",
                    borderRadius: "0.25rem",
                  }}
                />
                <Link
                  href={organization.instagramUrl}
                  target="_blank"
                  sx={{ alignSelf: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      overflowWrap: "break-word",
                      alignSelf: "center",
                      color: theme.palette.primary.main,
                    })}
                  >
                    {organization.instagramUrl}
                  </Typography>
                </Link>
              </InfoRow>
            )}
            {organization.twitterUrl && (
              <InfoRow>
                <X sx={{ width: "1.5rem", height: "1.5rem", color: "#000" }} />
                <Link
                  href={organization.twitterUrl}
                  target="_blank"
                  sx={{ alignSelf: "center" }}
                >
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      overflowWrap: "break-word",
                      alignSelf: "center",
                      color: theme.palette.primary.main,
                    })}
                  >
                    {organization.twitterUrl}
                  </Typography>
                </Link>
              </InfoRow>
            )}
          </Stack>
          <Separator />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: loading ? "center" : "space-evenly",
              padding: "0.5rem 1rem",
            }}
          >
            {loading ? (
              <LoadingSpinner />
            ) : (
              <Fragment>
                {!isOwnOrganization && (
                  <StyledButton
                    type="button"
                    variant="text"
                    onClick={showFollowPopup}
                    startIcon={
                      <NotificationsActiveOutlined
                        sx={{ width: "auto", height: "2rem", margin: 0 }}
                      />
                    }
                  >
                    {isFollowing ? "Following" : "Follow"}
                  </StyledButton>
                )}
                <StyledButton
                  type="button"
                  variant="text"
                  onClick={showSharePopup}
                  startIcon={
                    <IosShare
                      sx={{ width: "auto", height: "2rem", margin: 0 }}
                    />
                  }
                >
                  Share
                </StyledButton>
                <StyledButton
                  type="button"
                  variant="text"
                  onClick={showReportPopup}
                  startIcon={
                    <ReportOutlined
                      sx={{ width: "auto", height: "2rem", margin: 0 }}
                    />
                  }
                >
                  Report
                </StyledButton>
              </Fragment>
            )}
          </Box>
          <Separator />
          <UpcomingEvents
            {...{
              events,
              paginationStore,
              onPageChange,
              onViewEvent: (event) => onViewEvent(event),
            }}
          />
          <ConfirmModal
            store={confirmModalStore}
            title={popupState?.confirmTitle ?? ""}
            message={popupState?.confirmMessage ?? ""}
            onConfirm={popupState?.onConfirm ?? (() => {})}
            onCancel={() => confirmModalStore.close()}
          />
          <ShareModal
            store={shareModalStore}
            title="Share Organization"
            url={getOrganizationShareUrl(organizationId)}
          />
          <ReportEventModal
            store={reportModalStore}
            title="Report Organization"
            onSubmit={(reason) => {
              reportOrganization(reason);
              reportModalStore.close();
            }}
          />
        </Stack>
      </>
    );
  },
);

interface UpcomingEventsProps {
  events: Event[];
  paginationStore: PaginationStore;
  onPageChange: (newPage: number) => void;
  onViewEvent: (event: Event) => void;
}
const UpcomingEvents = observer(
  ({
    events,
    paginationStore,
    onPageChange,
    onViewEvent,
  }: UpcomingEventsProps) => {
    return (
      <Stack sx={{ gap: "0.75rem", padding: "0.5rem 1rem" }}>
        <SectionHeader>Upcoming Events</SectionHeader>
        {events.length > 0 ? (
          <EventList
            events={events}
            paginationStore={paginationStore}
            onPageChange={onPageChange}
            onViewEvent={(event) => onViewEvent(event)}
          />
        ) : (
          <Box
            sx={{
              display: "block",
              width: "100%",
              padding: "1rem",
              textAlign: "center",
            }}
          >
            <Typography variant="body2">
              No upcoming events were found.
            </Typography>
          </Box>
        )}
      </Stack>
    );
  },
);

const InfoRow = styled(Box)({
  display: "flex",
  gap: "1rem",
  alignItems: "baseline",
});

const SectionHeader = ({ children }: PropsWithChildren) => (
  <Typography
    variant="body2"
    sx={{ fontWeight: 700, size: "1rem", lineHeight: "1.5rem" }}
  >
    {children}
  </Typography>
);

const Separator = styled(Box)({
  height: "2px",
  width: "100%",
  backgroundColor: "#D9D9D9",
});

const StyledButton = styled(Button)(({ theme }) => ({
  flexDirection: "column",
  backgroundColor: "transparent",
  color: theme.palette.primary.main,
  "& .MuiButton-startIcon": {
    margin: 0,
  },
  textTransform: "none",
}));
